import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { GraduationCap, LogOut, Plus, TrendingUp, PiggyBank, Calendar, User, BookOpen, Camera, Save, Heart, History, Repeat, Gift, DollarSign, Clock, Upload, Search, Mail, Copy, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { NotificationsBell } from "./NotificationsBell";
import { UpcomingBirthdays } from "./UpcomingBirthdays";
import { SentGifts } from "./SentGifts";
import { MonthlySummary } from "./MonthlySummary";
import { SavingsGoal } from "./SavingsGoal";
import { NewChildForm } from "./ChildIdentity";
import { CurpBadge } from "./CurpBadge";
import { VerificationBanner } from "./VerificationBanner";
import { WelcomeTour } from "./WelcomeTour";
import { GettingStarted } from "./GettingStarted";
import { Textarea } from "../ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user, logout, updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("accounts");
  
  // Data States
  const [accounts, setAccounts] = useState([]);
  const [godchildren, setGodchildren] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [sentGifts, setSentGifts] = useState([]);
  const [giftMessage, setGiftMessage] = useState("");
  
  // UI States
  const [isNewAccountOpen, setIsNewAccountOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [verifyRequest, setVerifyRequest] = useState(0);

  const onboardingPending = !!user && !user.onboarding_seen;
  useEffect(() => {
    if (onboardingPending) setShowTour(true);
  }, [onboardingPending]);
  const [isContributionOpen, setIsContributionOpen] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  
  // Gift / Redeem States
  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeemTargetId, setRedeemTargetId] = useState(null);
  
  const [isDonorsOpen, setIsDonorsOpen] = useState(false);
  const [currentDonors, setCurrentDonors] = useState([]);
  
  const [searchAccountNum, setSearchAccountNum] = useState("");
  const [searchedChild, setSearchedChild] = useState(null);
  
  // Gift creation states
  const [giftEmail, setGiftEmail] = useState("");
  const [giftPhone, setGiftPhone] = useState("");
  const [shareInfo, setShareInfo] = useState(null);
  const [giftAmount, setGiftAmount] = useState(0);
  const [giftCustomAmount, setGiftCustomAmount] = useState("");
  const [giftFrequency, setGiftFrequency] = useState("one-time");
  const [giftDate, setGiftDate] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [emailHtml, setEmailHtml] = useState("");
  const [emailStatus, setEmailStatus] = useState("");
  const [previewEmail, setPreviewEmail] = useState("");

  const openSentGiftPreview = async (g) => {
      try {
          const res = await axios.get(`${BACKEND_URL}/api/gifts/${g.code}/email-preview`, { responseType: "text" });
          setGeneratedCode(g.code);
          setEmailHtml(res.data);
          setEmailStatus(g.email_status);
          setPreviewEmail(g.recipient_email || "");
          setShareInfo({ whatsapp_url: g.whatsapp_url, share_message: g.share_message, phone: g.recipient_phone });
          setShowEmailPreview(true);
      } catch (e) {
          toast.error("No se pudo cargar el correo");
      }
  };
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  
  // Profile State
  const [profileData, setProfileData] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    password: "",
    profile_picture: user?.profile_picture || ""
  });
  const [isUploading, setIsUploading] = useState(false);

  // Forms

  const [contributionData, setContributionData] = useState({
    amount: 0,
    type: "one-time", 
    scheduled_date: "",
    customAmount: ""
  });

  const PRESET_AMOUNTS = [100, 200, 500, 1000, 5000, 10000];

  // --- Data Fetching ---
  const fetchData = useCallback(async () => {
    try {
      const [accRes, godRes, txRes, recRes, notifRes, giftsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/accounts`),
        axios.get(`${BACKEND_URL}/api/godchildren`),
        axios.get(`${BACKEND_URL}/api/transactions`),
        axios.get(`${BACKEND_URL}/api/recurring`),
        axios.get(`${BACKEND_URL}/api/notifications`),
        axios.get(`${BACKEND_URL}/api/gifts/sent`)
      ]);
      setAccounts(accRes.data);
      setGodchildren(godRes.data);
      setTransactions(txRes.data);
      setRecurring(recRes.data);
      setNotifications(notifRes.data);
      setSentGifts(giftsRes.data);
    } catch (e) {
      console.error("Error fetching data", e);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab, fetchData]);

  useEffect(() => {
    const code = searchParams.get("gift");
    if (code) {
      setRedeemCode(code.toUpperCase());
      toast.info("Tienes un regalo pendiente. Abre una alcancía y usa 'Canjear regalo' para aplicarlo.", { duration: 8000 });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // --- Handlers ---
  const handleSendContribution = async () => {
    if (!selectedRecipient || !contributionData.amount || !contributionData.scheduled_date) {
        toast.error("Por favor completa todos los campos");
        return;
    }
    
    try {
        await axios.post(`${BACKEND_URL}/api/transactions`, {
            recipient_account_id: selectedRecipient.id,
            amount: parseFloat(contributionData.amount),
            type: contributionData.type,
            scheduled_date: contributionData.scheduled_date
        });
        toast.success("Contribución procesada con éxito! 🚀");
        setIsContributionOpen(false);
        setContributionData({ amount: 0, type: "one-time", scheduled_date: "", customAmount: "" });
        setSearchedChild(null); 
        setSearchAccountNum("");
        fetchData();
    } catch (e) {
        toast.error("Error al procesar la contribución");
    }
  };
  
  const handleRedeemGift = async () => {
      try {
          await axios.post(`${BACKEND_URL}/api/gifts/redeem`, {
              code: redeemCode,
              account_id: redeemTargetId
          });
          toast.success("¡Regalo canjeado exitosamente!");
          setIsRedeemOpen(false);
          setRedeemCode("");
          fetchData();
      } catch (e) {
          toast.error("Código inválido o expirado");
      }
  };
  
  const handleSearchAccount = async () => {
      if(!searchAccountNum) return;
      try {
          const res = await axios.get(`${BACKEND_URL}/api/accounts/search`, { params: { number: searchAccountNum } });
          setSearchedChild(res.data);
          toast.success("Cuenta encontrada");
      } catch (e) {
          toast.error("No se encontró ninguna cuenta con ese número");
          setSearchedChild(null);
      }
  };
  
  const handleCreateGift = async () => {
      if (!giftAmount || giftAmount <= 0 || (!giftEmail && !giftPhone)) {
          toast.error("Indica el correo o el celular del destinatario y un monto válido");
          return;
      }
      
      try {
          const res = await axios.post(`${BACKEND_URL}/api/gifts/create`, {
              amount: giftAmount,
              sender_name: user.full_name,
              recipient_email: giftEmail || null,
              recipient_phone: giftPhone || null,
              frequency: giftFrequency,
              scheduled_date: giftDate || null,
              message: giftMessage.trim() || null
          });
          setGeneratedCode(res.data.code);
          setEmailHtml(res.data.email_html || "");
          setEmailStatus(res.data.email_status || "");
          setPreviewEmail(giftEmail);
          setShareInfo({ whatsapp_url: res.data.whatsapp_url, share_message: res.data.share_message, phone: giftPhone });
          setShowEmailPreview(true);
          setGiftMessage("");
          fetchData();
          toast.success(res.data.email_status === "sent" ? "Regalo generado y correo enviado." : "Regalo generado. Compártelo por WhatsApp.");
      } catch (e) {
          toast.error(e.response?.data?.detail || "Error al generar el regalo");
      }
  };

  const copyText = async (text, ok) => {
      try { await navigator.clipboard.writeText(text); toast.success(ok); } catch { toast.error("No se pudo copiar"); }
  };
  const copyGiftCode = () => copyText(generatedCode, "Código copiado");
  
  const handleViewDonors = async (accountId) => {
      try {
          const res = await axios.get(`${BACKEND_URL}/api/accounts/${accountId}/donations`);
          setCurrentDonors(res.data);
          setIsDonorsOpen(true);
      } catch (e) {
          toast.error("Error al cargar padrinos");
      }
  };

  // Generic File Upload Handler
  const handleFileUpload = async (e, contextId = null, contextType = 'user') => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setIsUploading(true);
    try {
        const res = await axios.post(`${BACKEND_URL}/api/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const fullUrl = `${BACKEND_URL}${res.data.url}`;
        if (contextType === 'user') { setProfileData(prev => ({ ...prev, profile_picture: fullUrl })); } 
        else if (contextType === 'child') { await axios.put(`${BACKEND_URL}/api/accounts/${contextId}`, { profile_picture: fullUrl }); toast.success("Foto actualizada"); fetchData(); }
    } catch (error) { toast.error("Error al subir imagen"); } finally { setIsUploading(false); }
  };
  
  const handleUpdateProfile = async (e) => { e.preventDefault(); try { const updatePayload = { full_name: profileData.full_name, email: profileData.email, profile_picture: profileData.profile_picture }; if (profileData.password) updatePayload.password = profileData.password; const res = await axios.put(`${BACKEND_URL}/api/auth/me`, updatePayload); updateUser({ ...user, ...res.data }); setProfileData(prev => ({ ...prev, password: "" })); toast.success("Perfil actualizado"); } catch (error) { toast.error(error.response?.data?.detail || "Error al actualizar perfil"); } };
  const handleCancelRecurring = async (id) => { try { await axios.put(`${BACKEND_URL}/api/recurring/${id}`, null, { params: { status: 'cancelled' } }); toast.success("Suscripción cancelada"); fetchData(); } catch (e) { toast.error("Error al cancelar"); } };
  const calculateAge = (birthDate) => { const today = new Date(); const birth = new Date(birthDate); let age = today.getFullYear() - birth.getFullYear(); const m = today.getMonth() - birth.getMonth(); if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--; return age; };
  const calculateProjection = (balance, age) => { const yearsRemaining = 18 - age; if (yearsRemaining <= 0) return balance; return Math.round(balance * Math.pow(1.10, yearsRemaining)); };
  const openContributionModal = (recipient) => { setSelectedRecipient(recipient); setIsContributionOpen(true); };
  const canCreateAccounts = ["documents_received", "verified"].includes(user?.verification_status);
  const openGiftFromBirthday = (n) => openContributionModal({ id: n.account_id, child_name: n.child_name, child_lastname: n.child_lastname });

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-brand-dark">
      {/* Dashboard Header */}
      <nav className="bg-white border-b border-gray-200 px-4 md:px-8 h-20 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-2">
            <Link to="/dashboard">
                <GraduationCap className="w-8 h-8 text-brand-blue" />
            </Link>
            <span className="text-xl font-bold text-brand-blue hidden md:inline">Appadrino</span>
            <Link to="/educacion" className="ml-8 text-sm font-medium text-gray-500 hover:text-brand-blue flex items-center gap-1">
                <BookOpen className="w-4 h-4" /> Blog
            </Link>
        </div>
        <div className="flex items-center gap-3 md:gap-6">
            <NotificationsBell notifications={notifications} onGift={openGiftFromBirthday} />
            <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9 border border-gray-200">
                    <AvatarImage src={user?.profile_picture} alt={user?.full_name} />
                    <AvatarFallback className="bg-brand-lightBlue text-brand-blue font-bold">
                        {user?.full_name?.charAt(0) || "U"}
                    </AvatarFallback>
                </Avatar>
                <span className="text-gray-600 font-medium hidden sm:inline">{user?.full_name}</span>
            </div>
            <Button variant="ghost" onClick={logout} className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-2">
                <LogOut className="w-4 h-4" /> Salir
            </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 md:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <WelcomeTour open={showTour} userName={user?.first_name || user?.full_name?.split(" ")[0]} onClose={() => { setShowTour(false); if (!user.onboarding_seen) updateUser({ ...user, onboarding_seen: true }); }} />
            <TabsList className="bg-white p-1 rounded-xl shadow-sm border border-gray-100 flex flex-wrap h-auto">
                <TabsTrigger value="accounts" className="rounded-lg px-6 py-2 data-[state=active]:bg-brand-lightBlue data-[state=active]:text-brand-blue font-bold">
                    <PiggyBank className="w-4 h-4 mr-2" /> Mis Alcancías
                </TabsTrigger>
                <TabsTrigger value="godchildren" className="rounded-lg px-6 py-2 data-[state=active]:bg-brand-lightBlue data-[state=active]:text-brand-blue font-bold">
                    <Heart className="w-4 h-4 mr-2" /> Appadrina
                </TabsTrigger>
                <TabsTrigger value="history" className="rounded-lg px-6 py-2 data-[state=active]:bg-brand-lightBlue data-[state=active]:text-brand-blue font-bold">
                    <History className="w-4 h-4 mr-2" /> Historial
                </TabsTrigger>
                <TabsTrigger value="recurring" className="rounded-lg px-6 py-2 data-[state=active]:bg-brand-lightBlue data-[state=active]:text-brand-blue font-bold">
                    <Repeat className="w-4 h-4 mr-2" /> Recurrentes
                </TabsTrigger>
                <TabsTrigger value="profile" className="rounded-lg px-6 py-2 data-[state=active]:bg-brand-lightBlue data-[state=active]:text-brand-blue font-bold">
                    <User className="w-4 h-4 mr-2" /> Mi Perfil
                </TabsTrigger>
            </TabsList>

            {/* --- TAB: MIS ALCANCÍAS --- */}
            <TabsContent value="accounts" className="space-y-8">
                <VerificationBanner user={user} onUpdated={updateUser} openRequest={verifyRequest} />
                <GettingStarted
                    user={user} accounts={accounts} transactions={transactions} sentGifts={sentGifts}
                    onDismiss={() => updateUser({ ...user, checklist_dismissed: true })}
                    onReplayTour={() => setShowTour(true)}
                    onGoVerify={() => setVerifyRequest((n) => n + 1)}
                    onGoCreate={() => (canCreateAccounts ? setIsNewAccountOpen(true) : toast.error("Primero verifica tu identidad"))}
                    onGoGift={() => setActiveTab("godchildren")}
                />
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-extrabold text-brand-dark">Mis Alcancías</h1>
                        <p className="text-gray-500">Gestiona el futuro de tus hijos</p>
                    </div>
                    <Dialog open={isNewAccountOpen} onOpenChange={(o) => { if (o && !canCreateAccounts) { toast.error("Verifica tu identidad (CURP e INE) para crear alcancías"); return; } setIsNewAccountOpen(o); }}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-account-btn" className={`bg-brand-blue hover:bg-brand-blue/90 text-white font-bold rounded-full h-12 px-6 shadow-lg ${canCreateAccounts ? "" : "opacity-60"}`}>
                                <Plus className="w-5 h-5 mr-2" /> Nueva Cuenta
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[90vh] overflow-y-auto">
                            <DialogHeader><DialogTitle>Crear Alcancía</DialogTitle></DialogHeader>
                            <NewChildForm onCreated={() => { setIsNewAccountOpen(false); fetchData(); }} />
                        </DialogContent>
                    </Dialog>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {accounts.map((acc) => {
                        const age = calculateAge(acc.birth_date);
                        const projected = calculateProjection(acc.balance, age);
                        return (
                            <Card key={acc.id} className="border-none shadow-md hover:shadow-xl transition-all duration-300">
                                <CardHeader className="bg-gradient-to-r from-brand-blue to-cyan-600 text-white p-6 rounded-t-xl relative overflow-hidden">
                                    <div className="flex justify-between items-start z-10 relative">
                                        <div className="flex items-center gap-4">
                                            <div className="relative group">
                                                <Avatar className="w-16 h-16 border-2 border-white shadow-lg">
                                                    <AvatarImage src={acc.profile_picture} objectFit="cover" />
                                                    <AvatarFallback className="bg-white text-brand-blue font-bold text-xl">{acc.child_name.charAt(0)}</AvatarFallback>
                                                </Avatar>
                                                <label htmlFor={`child-upload-${acc.id}`} className="absolute bottom-0 right-0 bg-white text-brand-blue p-1 rounded-full cursor-pointer hover:bg-gray-100 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"><Camera className="w-3 h-3" /></label>
                                                <input id={`child-upload-${acc.id}`} type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e, acc.id, 'child')} />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold">{acc.child_name} {acc.child_lastname}{acc.child_second_lastname ? ` ${acc.child_second_lastname}` : ""}</h3>
                                                <div className="flex flex-wrap items-center gap-2 mt-1">
                                                    <div className="text-blue-100 text-xs bg-white/10 px-2 py-1 rounded inline-block">
                                                        CTA: {acc.account_number || "---"}
                                                    </div>
                                                    <CurpBadge account={acc} onUpdated={fetchData} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-6">
                                    <div className="space-y-4">
                                        <div><p className="text-sm text-gray-500 font-bold">Saldo Actual</p><p className="text-3xl font-extrabold text-brand-dark">${acc.balance.toLocaleString()}</p></div>
                                        <div className="bg-brand-lightGreen/50 p-4 rounded-xl border border-brand-green/20">
                                            <p className="text-xs font-bold text-brand-green uppercase">Proyección 18 años</p>
                                            <p className="text-2xl font-bold text-brand-dark">${projected.toLocaleString()}</p>
                                        </div>
                                        <SavingsGoal account={acc} projected={projected} onUpdated={fetchData} />
                                        <div className="grid grid-cols-2 gap-2">
                                            <Button className="bg-brand-blue font-bold text-white shadow-sm" onClick={() => openContributionModal(acc)}>
                                                <DollarSign className="w-4 h-4 mr-1" /> Contribuir
                                            </Button>
                                            <Button variant="outline" className="border-brand-green text-brand-dark font-bold hover:bg-brand-green/10" onClick={() => { setRedeemTargetId(acc.id); setIsRedeemOpen(true); }}>
                                                <Gift className="w-4 h-4 mr-1" /> Canjear
                                            </Button>
                                        </div>
                                        <Button variant="ghost" className="w-full text-xs text-gray-400 hover:text-brand-blue" onClick={() => handleViewDonors(acc.id)}>
                                            Ver Padrinos
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </TabsContent>

            {/* --- TAB: APPADRINA --- */}
            <TabsContent value="godchildren" className="space-y-8">
                <div className="flex flex-col gap-8">
                    <UpcomingBirthdays notifications={notifications} onGift={openGiftFromBirthday} />
                    <div className="grid md:grid-cols-2 gap-8">
                        {/* Search Section */}
                        <Card className="flex-1 border-none shadow-md">
                            <CardHeader><CardTitle>Apadrinar por Número de Cuenta</CardTitle><CardDescription>Si ya conoces a tu ahijado, ingresa su número de cuenta.</CardDescription></CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex gap-2">
                                    <Input placeholder="Ej: SOF123456" value={searchAccountNum} onChange={(e) => setSearchAccountNum(e.target.value)} />
                                    <Button onClick={handleSearchAccount}><Search className="w-4 h-4" /></Button>
                                </div>
                                
                                {searchedChild && (
                                    <div className="mt-4 p-4 border rounded-xl bg-brand-lightGreen/20 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <Avatar>
                                                <AvatarImage src={searchedChild.profile_picture} />
                                                <AvatarFallback>{searchedChild.child_name.charAt(0)}</AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <p className="font-bold text-brand-dark">{searchedChild.child_name} {searchedChild.child_lastname}</p>
                                                <p className="text-xs text-gray-500">Cuenta Verificada</p>
                                            </div>
                                        </div>
                                        <Button className="bg-brand-green text-brand-dark font-bold" onClick={() => openContributionModal(searchedChild)}>
                                            Apadrinar
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                        
                        {/* Gift Section */}
                        <Card className="flex-1 border-none shadow-md">
                            <CardHeader><CardTitle>Regalar a un Amigo</CardTitle><CardDescription>Apadrina al hijo de un amigo o familiar: envía la invitación por correo, WhatsApp o ambos.</CardDescription></CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Correo del destinatario</Label>
                                        <Input type="email" data-testid="gift-email-input" placeholder="amigo@email.com" value={giftEmail} onChange={(e) => setGiftEmail(e.target.value)} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Celular (WhatsApp)</Label>
                                        <Input type="tel" data-testid="gift-phone-input" placeholder="55 1234 5678" value={giftPhone} onChange={(e) => setGiftPhone(e.target.value.replace(/[^\d+\s-]/g, ""))} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Monto ($)</Label>
                                    <div className="flex gap-2">
                                        <Select onValueChange={(val) => setGiftAmount(parseFloat(val))}>
                                            <SelectTrigger className="w-32"><SelectValue placeholder="Monto" /></SelectTrigger>
                                            <SelectContent>
                                                {PRESET_AMOUNTS.map(a => <SelectItem key={a} value={a.toString()}>${a}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <Input 
                                            placeholder="Otro" 
                                            data-testid="gift-custom-amount-input"
                                            type="number" 
                                            value={giftCustomAmount} 
                                            onChange={(e) => { setGiftCustomAmount(e.target.value); setGiftAmount(parseFloat(e.target.value)); }} 
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Frecuencia</Label>
                                        <Select value={giftFrequency} onValueChange={setGiftFrequency}>
                                            <SelectTrigger><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="one-time">Único</SelectItem>
                                                <SelectItem value="monthly">Mensual</SelectItem>
                                                <SelectItem value="annual">Anual</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Fecha (opcional)</Label>
                                        <Input type="date" data-testid="gift-date-input" value={giftDate} onChange={e => setGiftDate(e.target.value)} />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label>Dedicatoria (opcional)</Label>
                                        <span className={`text-xs ${giftMessage.length > 280 ? "text-red-500" : "text-gray-400"}`} data-testid="gift-message-counter">{giftMessage.length}/300</span>
                                    </div>
                                    <Textarea data-testid="gift-message-input" maxLength={300} rows={3} placeholder="Ej: Para que sigas soñando en grande. ¡Feliz cumpleaños! Con cariño, tu madrina." value={giftMessage} onChange={(e) => setGiftMessage(e.target.value)} className="resize-none rounded-xl" />
                                </div>
                                
                                <Button className="w-full bg-brand-blue font-bold" data-testid="gift-generate-btn" onClick={handleCreateGift}>
                                    <Mail className="w-4 h-4 mr-2" /> Generar regalo e invitar
                                </Button>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sent Gifts */}
                    <SentGifts gifts={sentGifts} onRefresh={fetchData} onPreview={openSentGiftPreview} />

                    {/* Godchildren Grid */}
                    {godchildren.length > 0 && (
                        <div>
                            <h3 className="text-xl font-bold text-brand-dark mb-4">Tus Ahijados</h3>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {godchildren.map((child) => (
                                    <Card key={child.id} className="border-none shadow-md hover:shadow-xl transition-all">
                                        <CardHeader className="bg-brand-lightGreen/30 p-6 rounded-t-xl">
                                            <div className="flex items-center gap-4">
                                                <Avatar className="w-12 h-12 bg-white text-brand-green shadow-sm">
                                                    <AvatarImage src={child.profile_picture} />
                                                    <AvatarFallback>{child.child_name.charAt(0)}</AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <h3 className="text-xl font-bold text-brand-dark">{child.child_name} {child.child_lastname}</h3>
                                                    <p className="text-sm text-gray-600">Futuro Universitario</p>
                                                </div>
                                            </div>
                                        </CardHeader>
                                        <CardContent className="p-6">
                                            <div className="mb-6 bg-white p-3 rounded-xl border border-gray-100">
                                                <p className="text-xs text-gray-500 uppercase font-bold text-center">Tu Contribución Total</p>
                                                <p className="text-2xl font-extrabold text-brand-green text-center">${child.total_donated?.toLocaleString() || 0}</p>
                                            </div>
                                            <Button 
                                                className="w-full bg-brand-green text-brand-dark font-bold hover:bg-brand-green/90 shadow-lg shadow-brand-green/20"
                                                onClick={() => openContributionModal(child)}
                                            >
                                                <Gift className="w-4 h-4 mr-2" /> Enviar Regalo
                                            </Button>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </TabsContent>

            {/* --- MODALS --- */}
            
            {/* Email Preview Modal */}
            <Dialog open={showEmailPreview} onOpenChange={setShowEmailPreview}>
                <DialogContent className="max-w-2xl" data-testid="gift-email-preview-dialog">
                    <DialogHeader>
                        <DialogTitle>{emailHtml && previewEmail ? "Vista previa del correo" : "Comparte el regalo"}</DialogTitle>
                        <p className="text-sm text-gray-500" data-testid="gift-email-status">
                            {emailStatus === "sent"
                              ? <>Correo enviado a <span className="font-medium text-brand-dark">{previewEmail}</span>.</>
                              : emailStatus === "no_recipient" || !previewEmail
                                ? <>Este regalo se comparte por celular. Envíalo por WhatsApp o copia el mensaje.</>
                                : <>Así se verá el correo para <span className="font-medium text-brand-dark">{previewEmail}</span>. {emailStatus === "failed" ? "No se pudo enviar; comparte el código manualmente." : "El envío está pendiente de configuración."}</>}
                        </p>
                    </DialogHeader>
                    <div className="flex items-center justify-between rounded-xl bg-brand-lightBlue/60 px-4 py-3">
                        <div>
                            <p className="text-xs uppercase font-bold text-gray-500">Código de canje</p>
                            <p data-testid="gift-generated-code" className="text-2xl font-mono tracking-widest text-brand-blue font-bold">{generatedCode}</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={copyGiftCode} data-testid="gift-copy-code-btn" className="gap-2 border-brand-blue text-brand-blue"><Copy className="w-4 h-4" /> Copiar</Button>
                    </div>
                    {shareInfo && (
                        <div className="flex flex-col sm:flex-row gap-2" data-testid="gift-share-actions">
                            <a href={shareInfo.whatsapp_url} target="_blank" rel="noreferrer" data-testid="gift-whatsapp-btn" className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white font-bold text-sm transition-colors">
                                <MessageCircle className="w-4 h-4" /> {shareInfo.phone ? `Enviar por WhatsApp al ${shareInfo.phone}` : "Enviar por WhatsApp"}
                            </a>
                            <Button variant="outline" data-testid="gift-copy-message-btn" onClick={() => copyText(shareInfo.share_message, "Mensaje copiado, pégalo en SMS o Telegram")} className="flex-1 h-11 rounded-full gap-2 border-gray-300">
                                <Copy className="w-4 h-4" /> Copiar mensaje
                            </Button>
                        </div>
                    )}
                    <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
                        <iframe title="Vista previa del correo" srcDoc={emailHtml} data-testid="gift-email-iframe" className="w-full h-[520px] bg-white" sandbox="" />
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Other Modals (Redeem, Contribution, Donors) */}
            {/* ... Same as previous implementation, ensuring Contribution Modal supports Annual ... */}
            <Dialog open={isContributionOpen} onOpenChange={setIsContributionOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Contribuir a {selectedRecipient?.child_name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 pt-4">
                        <div className="space-y-3">
                            <Label>Selecciona un monto</Label>
                            <div className="flex flex-wrap gap-2">
                                {PRESET_AMOUNTS.map(amount => (
                                    <button key={amount} onClick={() => setContributionData({ ...contributionData, amount: amount, customAmount: "" })}
                                        className={`px-3 py-1 rounded-full text-sm font-bold border transition-colors ${contributionData.amount === amount ? 'bg-brand-blue text-white border-brand-blue' : 'bg-white text-gray-600 border-gray-200 hover:border-brand-blue'}`}>${amount}</button>
                                ))}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">Otra:</span>
                                <Input type="number" placeholder="$0.00" value={contributionData.customAmount} 
                                    onChange={(e) => { const val = parseFloat(e.target.value); setContributionData({ ...contributionData, customAmount: e.target.value, amount: val }); }}
                                    className="w-32 h-8 text-sm" />
                            </div>
                        </div>
                        <div className="space-y-3">
                            <Label>Frecuencia</Label>
                            <Tabs value={contributionData.type} onValueChange={(val) => setContributionData({ ...contributionData, type: val })} className="w-full">
                                <TabsList className="w-full grid grid-cols-3">
                                    <TabsTrigger value="one-time">Único</TabsTrigger>
                                    <TabsTrigger value="monthly">Mensual</TabsTrigger>
                                    <TabsTrigger value="annual">Anual</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                        <div className="space-y-3">
                            <Label>{contributionData.type === 'one-time' ? 'Fecha del cargo' : 'Día del cargo (1-31)'}</Label>
                            {contributionData.type === 'one-time' ? (
                                <Input type="date" value={contributionData.scheduled_date} onChange={(e) => setContributionData({ ...contributionData, scheduled_date: e.target.value })} />
                            ) : (
                                <Select onValueChange={(val) => setContributionData({ ...contributionData, scheduled_date: val })}>
                                    <SelectTrigger><SelectValue placeholder="Selecciona el día" /></SelectTrigger>
                                    <SelectContent>{[1, 5, 10, 15, 20, 25, 30].map(day => (<SelectItem key={day} value={day.toString()}>Día {day}</SelectItem>))}</SelectContent>
                                </Select>
                            )}
                        </div>
                        <Button className="w-full bg-brand-blue font-bold text-lg h-12" onClick={handleSendContribution}>Confirmar</Button>
                    </div>
                </DialogContent>
            </Dialog>
            
            <Dialog open={isDonorsOpen} onOpenChange={setIsDonorsOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Padrinos</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-2">
                        {currentDonors.length === 0 ? <p className="text-center text-gray-500">Aún no hay padrinos externos.</p> : (
                            currentDonors.map((d, i) => (
                                <div key={i} className="flex justify-between items-center p-3 border rounded-lg">
                                    <div><p className="font-bold">{d.donor_name}</p><p className="text-xs text-gray-400">{new Date(d.date).toLocaleDateString()}</p></div>
                                    <p className="font-bold text-green-600">+${d.amount}</p>
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>
            
            <Dialog open={isRedeemOpen} onOpenChange={setIsRedeemOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Canjear Regalo</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-4">
                        <Label>Ingresa el código de regalo</Label>
                        <Input placeholder="XXXX-XXXX" value={redeemCode} onChange={(e) => setRedeemCode(e.target.value)} className="text-center text-xl tracking-widest uppercase" />
                        <Button className="w-full bg-brand-green text-brand-dark font-bold" onClick={handleRedeemGift}>Canjear Ahora</Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Other Tabs (Historial, Recurring, Profile) - Keep existing */}
            <TabsContent value="history" className="space-y-8">
                <MonthlySummary accounts={accounts} />
                <Card>
                    <CardHeader><CardTitle>Historial de Transacciones</CardTitle></CardHeader>
                    <CardContent>
                        {transactions.length === 0 ? <p className="text-gray-500">No hay movimientos registrados.</p> : (
                            <div className="space-y-4">
                                {transactions.map((tx) => (
                                    <div key={tx.id} className="flex justify-between items-center p-4 border rounded-xl bg-gray-50/50">
                                        <div className="flex items-center gap-4">
                                            <div className={`p-2 rounded-full ${tx.type === 'monthly_setup' ? 'bg-purple-100 text-purple-600' : 'bg-green-100 text-green-600'}`}>
                                                {tx.type === 'monthly_setup' ? <Repeat className="w-5 h-5" /> : <DollarSign className="w-5 h-5" />}
                                            </div>
                                            <div>
                                                <p className="font-bold text-brand-dark">{tx.recipient_name}</p>
                                                <p className="text-sm text-gray-500 capitalize">{tx.type === 'monthly_setup' ? 'Suscripción Mensual' : tx.type === 'gift_redemption' ? 'Canje de Regalo' : 'Contribución'}</p>
                                                <p className="text-xs text-gray-400">{new Date(tx.created_at).toLocaleDateString()}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className="font-bold text-lg">${tx.amount}</p>
                                            <Badge variant={tx.status === 'completed' ? 'default' : 'secondary'} className={tx.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}>
                                                {tx.status}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="recurring">
                <Card>
                    <CardHeader><CardTitle>Suscripciones Activas</CardTitle></CardHeader>
                    <CardContent>
                        {recurring.length === 0 ? <p className="text-gray-500">No tienes suscripciones activas.</p> : (
                            <div className="grid gap-4">
                                {recurring.map((rec) => (
                                    <div key={rec.id} className="flex justify-between items-center p-4 border border-brand-blue/20 rounded-xl bg-brand-lightBlue/10">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2 bg-brand-blue text-white rounded-lg">
                                                <Clock className="w-6 h-6" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-lg">{rec.recipient_name}</p>
                                                <p className="text-sm text-gray-600">Cargo mensual: Día {rec.day_of_month}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-6">
                                            <p className="font-bold text-xl text-brand-blue">${rec.amount}/mes</p>
                                            <Button variant="destructive" size="sm" onClick={() => handleCancelRecurring(rec.id)}>
                                                Cancelar
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="profile">
                {/* Profile content */}
                <div className="grid md:grid-cols-3 gap-8">
                    <div className="md:col-span-1 space-y-6">
                        <Card className="border-none shadow-md overflow-hidden">
                            <CardContent className="pt-6 flex flex-col items-center">
                                <div className="relative group">
                                    <Avatar className="w-32 h-32 border-4 border-brand-lightBlue">
                                        <AvatarImage src={profileData.profile_picture} objectFit="cover" />
                                        <AvatarFallback className="text-4xl bg-brand-lightBlue text-brand-blue">{user?.full_name?.charAt(0) || "U"}</AvatarFallback>
                                    </Avatar>
                                    <label htmlFor="picture-upload" className="absolute bottom-0 right-0 bg-brand-blue text-white p-2 rounded-full cursor-pointer hover:bg-brand-blue/90 shadow-lg"><Camera className="w-5 h-5" /></label>
                                    <input id="picture-upload" type="file" className="hidden" accept="image/*" onChange={(e) => handleFileUpload(e)} disabled={isUploading} />
                                </div>
                                <h2 className="mt-4 text-xl font-bold text-brand-dark">{user?.full_name}</h2>
                                <p className="text-gray-500 text-sm">{user?.email}</p>
                                <div className="mt-4 w-full rounded-xl bg-gray-50 border border-gray-100 p-3 text-xs space-y-1" data-testid="profile-verification">
                                    <p className="font-bold text-gray-500 uppercase tracking-wide">Identidad del tutor</p>
                                    <p className="text-brand-dark">{user?.curp_masked ? `CURP ${user.curp_masked}` : "CURP no registrado"}</p>
                                    <p className={`font-bold ${user?.verification_status === "verified" ? "text-brand-green" : user?.verification_status === "documents_received" ? "text-brand-blue" : "text-amber-600"}`} data-testid="profile-verification-status">
                                        {{ verified: "Verificado", documents_received: "INE recibido · revisión pendiente", pending_documents: "Falta subir INE", pending_identity: "Falta registrar CURP" }[user?.verification_status] || "—"}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                    <div className="md:col-span-2">
                        <Card className="border-none shadow-md">
                            <CardHeader><CardTitle>Editar Información</CardTitle></CardHeader>
                            <CardContent>
                                <form onSubmit={handleUpdateProfile} className="space-y-6">
                                    <div className="space-y-2"><Label>Nombre Completo</Label><Input value={profileData.full_name} onChange={(e) => setProfileData({...profileData, full_name: e.target.value})} /></div>
                                    <div className="space-y-2"><Label>Correo</Label><Input type="email" value={profileData.email} onChange={(e) => setProfileData({...profileData, email: e.target.value})} /></div>
                                    <div className="space-y-2"><Label>Nueva Contraseña</Label><Input type="password" placeholder="••••••••" value={profileData.password} onChange={(e) => setProfileData({...profileData, password: e.target.value})} /></div>
                                    <div className="pt-4 flex justify-end"><Button type="submit" className="bg-brand-blue font-bold text-white"><Save className="w-4 h-4 mr-2" /> Guardar</Button></div>
                                </form>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
